import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useInsights } from '../hooks/useInsights';

const SERVICE_INFO = {
  'LinkedIn_Automation': { icon: '🔗', short: 'LinkedIn', desc: 'Finding and connecting with target prospects on LinkedIn' },
  'Email_Marketing':     { icon: '📧', short: 'Email', desc: 'Running targeted email campaigns to reach potential customers' },
  'Content_Creation':    { icon: '✍️', short: 'Content', desc: 'Writing blogs, newsletters, and SEO content' },
  'SEO':                 { icon: '🔍', short: 'SEO', desc: 'Improving search engine rankings and organic traffic' },
  'BD_for_Clients':      { icon: '🤝', short: 'Business Dev', desc: 'Finding and introducing potential partners or customers' },
  'Retention_Marketing': { icon: '🔄', short: 'Retention', desc: 'Keeping existing customers engaged and reducing churn' },
  'Podcast_&_Video_Production': { icon: '🎬', short: 'Podcast & Video', desc: 'Producing podcast episodes and video content' },
  'Website_UI/UX_Design':      { icon: '🎨', short: 'Website Design', desc: 'Designing website layouts and user experience' },
  'Website_Development':        { icon: '💻', short: 'Website Dev', desc: 'Building and maintaining websites' },
  'Performance_Marketing':      { icon: '📈', short: 'Paid Ads', desc: 'Running paid advertising campaigns for leads and sales' },
  'Media_&_PR':                 { icon: '📰', short: 'Media & PR', desc: 'Getting press coverage and managing media relations' },
  'Logo_&_Brand_Guidelines':   { icon: '🏷️', short: 'Branding', desc: 'Creating brand identity, logos, and visual guidelines' },
  'Creative_&_Video':           { icon: '🎥', short: 'Creative & Video', desc: 'Creating ad creatives, videos, and visual assets' },
  'Outsourced_Head_of_Operations': { icon: '📋', short: 'Operations', desc: 'Managing day-to-day operations and processes' }
};

const STATUS = {
  'On Track':  { icon: '✅', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', soft: '#dcfce7' },
  'At Risk':   { icon: '⚠️', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', soft: '#ffedd5' },
  'Delayed':   { icon: '🔴', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', soft: '#fee2e2' },
  'Unknown':   { icon: '⚪', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', soft: '#f3f4f6' }
};

function norm(s) {
  if (!s) return 'Unknown';
  const l = s.toLowerCase();
  if (l.includes('track')) return 'On Track';
  if (l.includes('risk') || l.includes('off track')) return 'At Risk';
  if (l.includes('delay') || l.includes('blocked')) return 'Delayed';
  return 'Unknown';
}

const FALSE_BLOCKERS = new Set(['on schedule — no issues','on schedule - no issues','on schedule','no issues','none','n/a','na','-','','no blockers','no blockers at the moment','no current blockers']);

function relDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = Math.floor((Date.now() - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff} days ago`;
  if (diff < 30) return `${Math.floor(diff/7)}w ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildWhatWeDo(history) {
  if (!history || !history.length) return [];
  const byForm = {};
  for (const h of history) {
    if (!byForm[h.form]) byForm[h.form] = h;
  }
  return Object.entries(byForm).map(([form, entry]) => {
    const info = SERVICE_INFO[form] || { icon: '📌', short: form, desc: form };
    const rc = entry.richContent || {};
    let concrete = '';
    if (form === 'BD_for_Clients') {
      concrete = rc.activity || rc.notes || '';
      if (concrete.length > 80) concrete = concrete.slice(0, 80) + '…';
      if (!concrete) concrete = 'Identifying and connecting with potential partners';
    } else if (form === 'LinkedIn_Automation') {
      concrete = rc.tool ? `LinkedIn outreach campaigns via ${rc.tool}` : 'LinkedIn outreach campaigns';
    } else if (form === 'Email_Marketing') {
      concrete = rc.emailsSent ? `Email campaigns: ${rc.emailsSent}` : 'Targeted email campaigns';
    } else if (form === 'Content_Creation') {
      concrete = rc.publishedVsTarget || rc.contentDelivered || 'Blog and newsletter content';
    } else if (form === 'SEO') {
      concrete = rc.keywordMovements || 'SEO optimization and keyword rankings';
    } else if (form === 'Retention_Marketing') {
      concrete = rc.campaignsExecuted ? rc.campaignsExecuted.slice(0, 80) : 'CRM campaigns and customer retention';
    } else if (form === 'Podcast_&_Video_Production') {
      concrete = rc.episodesDelivered ? `${rc.episodesDelivered} produced` : 'Podcast and video production';
    } else {
      concrete = info.desc;
    }
    return { form, icon: info.icon, short: info.short, desc: info.desc, concrete, updates: history.filter(h => h.form === form).length };
  });
}

function buildScope(history) {
  if (!history || !history.length) return null;
  const scopeLines = [];
  const metrics = [];
  const seen = new Set();
  for (const h of history) {
    const rc = h.richContent || {};
    if (rc.scope && rc.scope !== 'N/A' && rc.scope.trim() && !seen.has('s')) { seen.add('s'); scopeLines.push(rc.scope.trim()); }
    if (rc.publishedVsTarget && rc.publishedVsTarget !== 'N/A' && !seen.has('p')) { seen.add('p'); metrics.push(rc.publishedVsTarget); }
    if (rc.episodesDelivered && !seen.has('e')) { seen.add('e'); metrics.push(rc.episodesDelivered + ' episodes delivered'); }
    if (rc.activeVsTotal && rc.activeVsTotal !== 'N/A' && !seen.has('a')) { seen.add('a'); metrics.push(rc.activeVsTotal + ' campaigns active'); }
    if (rc.kpiVsTarget && rc.kpiVsTarget !== 'N/A' && !seen.has('k')) { seen.add('k'); metrics.push(rc.kpiVsTarget); }
  }
  return scopeLines.length || metrics.length ? { scopeLines, metrics } : null;
}

// ─── Extract 5 separate items: pending, blocked, reasons, wins, follow-ups ──
function buildProjectFlags(project, history) {
  const pending = [];    // Planned next actions
  const blocked = [];    // What task/work is stuck
  const reasons = [];   // Why it's delayed
  const wins = [];       // What's working well
  const followups = [];  // Needs follow-up

  if (history && history.length) {
    const byForm = {};
    for (const h of history) {
      if (!byForm[h.form]) byForm[h.form] = h;
    }
    for (const [form, entry] of Object.entries(byForm)) {
      const info = SERVICE_INFO[form] || { icon: '📌', short: form };
      const rc = entry.richContent || {};

      // Pending / Next Steps
      if (rc.planNextWeek && rc.planNextWeek !== 'N/A' && rc.planNextWeek !== 'No Plans' && rc.planNextWeek.trim()) {
        pending.push({ service: info.short, icon: info.icon, text: rc.planNextWeek.trim() });
      }

      // What's blocked (the task that's stuck)
      if (entry.blockers && entry.blockers.trim() && !FALSE_BLOCKERS.has(entry.blockers.trim().toLowerCase())) {
        blocked.push({ service: info.short, icon: info.icon, text: entry.blockers.trim() });
      }

      // Why it's delayed (flags give the reason, notes give the explanation)
      if (rc.flags && !FALSE_BLOCKERS.has(rc.flags.trim().toLowerCase())) {
        const noteText = (rc.notes && rc.notes !== 'N/A' && rc.notes !== 'No updates') ? rc.notes.trim() : null;
        reasons.push({
          service: info.short, icon: info.icon,
          flag: rc.flags.trim(),
          explanation: noteText ? (noteText.length > 150 ? noteText.slice(0, 150) + '…' : noteText) : null
        });
      }

      // What's working well — completions, positive signals, wins
      if (rc.completedThisWeek && rc.completedThisWeek !== 'N/A' && rc.completedThisWeek.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: rc.completedThisWeek.trim() });
      } else if (rc.contentDelivered && rc.contentDelivered !== 'N/A' && rc.contentDelivered.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: rc.contentDelivered.trim() });
      } else if (rc.deliveryThisWeek && rc.deliveryThisWeek !== 'N/A' && rc.deliveryThisWeek.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: rc.deliveryThisWeek.trim() });
      }
      if (rc.conversions && rc.conversions !== 'N/A' && rc.conversions.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: `Conversions: ${rc.conversions.trim()}` });
      }
      if (rc.meetingsHeld && rc.meetingsHeld !== 'N/A' && rc.meetingsHeld.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: `Meetings held: ${rc.meetingsHeld.trim()}` });
      }
      if (rc.renewalsUpsells && rc.renewalsUpsells !== 'N/A' && rc.renewalsUpsells.trim()) {
        wins.push({ service: info.short, icon: info.icon, text: rc.renewalsUpsells.trim().substring(0, 120) });
      }
      if (rc.satisfactionSignals && rc.satisfactionSignals !== 'N/A' && !rc.satisfactionSignals.toLowerCase().startsWith('no negative')) {
        wins.push({ service: info.short, icon: info.icon, text: rc.satisfactionSignals.trim().substring(0, 120) });
      }

      // Needs follow-up — items requiring explicit follow-up action
      const notesLower = (rc.notes || '').toLowerCase();
      if (notesLower.includes('follow up') || notesLower.includes('follow-up') || notesLower.includes('followup') || notesLower.includes('awaiting') || notesLower.includes('waiting for') || notesLower.includes('pending from')) {
        const followText = rc.notes.trim().substring(0, 120);
        followups.push({ service: info.short, icon: info.icon, text: followText });
      }
      if (rc.contentStatus && rc.contentStatus !== 'N/A' && (rc.contentStatus.toLowerCase().includes('awaiting') || rc.contentStatus.toLowerCase().includes('pending') || rc.contentStatus.toLowerCase().includes('approval'))) {
        followups.push({ service: info.short, icon: info.icon, text: rc.contentStatus.trim().substring(0, 120) });
      }
    }
  }

  // Deduplicate
  const uniqueBlocked = []; const seenB = new Set();
  for (const b of blocked) { const k = b.text.substring(0, 30); if (!seenB.has(k)) { seenB.add(k); uniqueBlocked.push(b); } }
  const uniqueReasons = []; const seenR = new Set();
  for (const r of reasons) { const k = r.flag.substring(0, 30); if (!seenR.has(k)) { seenR.add(k); uniqueReasons.push(r); } }
  const uniqueWins = []; const seenW = new Set();
  for (const w of wins) { const k = w.service + ':' + w.text.substring(0, 30); if (!seenW.has(k)) { seenW.add(k); uniqueWins.push(w); } }
  const uniqueFollowups = []; const seenF = new Set();
  for (const f of followups) { const k = f.service + ':' + f.text.substring(0, 30); if (!seenF.has(k)) { seenF.add(k); uniqueFollowups.push(f); } }

  return {
    pending: pending.slice(0, 3),
    blocked: uniqueBlocked.slice(0, 3),
    reasons: uniqueReasons.slice(0, 3),
    wins: uniqueWins.slice(0, 3),
    followups: uniqueFollowups.slice(0, 3)
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════
function Dashboard() {
  const { data, loading, error } = useInsights();
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAM, setFilterAM] = useState('all');
  const [search, setSearch] = useState('');

  const projects = data?.projects || [];
  const ams = data?.amList || [];
  const stats = data?.summary_stats || {};

  const filtered = useMemo(() => {
    let list = [...projects];
    if (filterAM !== 'all') list = list.filter(p => p.account_manager === filterAM);
    if (filterStatus !== 'all') {
      if (filterStatus === 'needs_attention') list = list.filter(p => p.overall_status === 'At Risk' || p.overall_status === 'Delayed');
      else list = list.filter(p => p.overall_status === filterStatus);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.project_name.toLowerCase().includes(q) ||
        p.account_manager.toLowerCase().includes(q) ||
        (p.latest_update || '').toLowerCase().includes(q)
      );
    }
    const order = { 'Delayed': 0, 'At Risk': 1, 'Unknown': 2, 'On Track': 3 };
    list.sort((a, b) => (order[a.overall_status] ?? 2) - (order[b.overall_status] ?? 2) || a.project_name.localeCompare(b.project_name));
    return list;
  }, [projects, filterAM, filterStatus, search]);

  const lastUpdated = data?.generatedAt ? new Date(data.generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const atRiskCount = (stats.at_risk || 0) + (stats.delayed || 0);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState />;

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <header style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '16px 24px', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111', margin: 0, letterSpacing: -0.5 }}>
                IncX<span style={{ color: '#6366f1' }}>Ops</span>
              </h1>
              <p style={{ fontSize: 12, color: '#9ca3af', margin: '2px 0 0 0' }}>
                Client project tracker · Updated {lastUpdated || 'loading...'} · {projects.length} projects
              </p>
            </div>
            {atRiskCount > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#dc2626' }}>
                ⚠️ {atRiskCount} project{atRiskCount > 1 ? 's' : ''} need{atRiskCount === 1 ? 's' : ''} attention
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="text" placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: '1 1 200px', padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', background: '#f9fafb' }} />
            <select value={filterAM} onChange={e => setFilterAM(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#f9fafb', cursor: 'pointer' }}>
              <option value="all">All Team Members</option>
              {ams.map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 6 }}>
              <Pill label="✅ On Track" count={stats.on_track || 0} color="#16a34a" active={filterStatus === 'On Track'} onClick={() => setFilterStatus(filterStatus === 'On Track' ? 'all' : 'On Track')} />
              <Pill label="⚠️ Needs Attention" count={atRiskCount} color="#ea580c" active={filterStatus === 'needs_attention'} onClick={() => setFilterStatus(filterStatus === 'needs_attention' ? 'all' : 'needs_attention')} />
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 24px' }}>
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>
          Showing {filtered.length} of {projects.length} projects
          {filterAM !== 'all' && <> · <strong style={{ color: '#6366f1' }}>{filterAM}</strong></>}
        </div>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
            <p style={{ fontSize: 16 }}>No projects match your filters</p>
            <button onClick={() => { setFilterAM('all'); setFilterStatus('all'); setSearch(''); }}
              style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: 14 }}>
              Clear filters
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
            {filtered.map(project => <ProjectCard key={project.id} project={project} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function Pill({ label, count, color, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20,
      border: active ? `2px solid ${color}` : '1px solid #e5e7eb', background: active ? `${color}15` : 'white',
      cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 500, color: active ? color : '#6b7280', whiteSpace: 'nowrap'
    }}>
      {label} ({count})
    </button>
  );
}

// ─── Project Card — with separate pending/blocked/reason sections ─────────
function ProjectCard({ project }) {
  const cfg = STATUS[project.overall_status] || STATUS['Unknown'];
  const services = buildWhatWeDo(project.history);
  const scope = buildScope(project.history);
  const { pending, blocked, reasons, wins, followups } = buildProjectFlags(project, project.history);
  const lastDate = project.last_update ? relDate(project.last_update) : '';

  return (
    <Link to={`/project/${encodeURIComponent(project.project_name)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <div style={{
        background: 'white', borderRadius: 12, border: `1px solid ${cfg.border}`, borderTop: `4px solid ${cfg.color}`,
        padding: 20, transition: 'box-shadow 0.2s, transform 0.2s', cursor: 'pointer'
      }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
      >
        {/* Name + Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111', margin: 0, lineHeight: 1.3 }}>
            {project.project_name}
          </h3>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 16,
            fontSize: 12, fontWeight: 600, background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap', marginLeft: 8, flexShrink: 0
          }}>
            {cfg.icon} {project.overall_status === 'At Risk' ? 'Needs Attention' : project.overall_status}
          </span>
        </div>

        {/* Services tags */}
        {services.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
            {services.map(s => (
              <span key={s.form} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, background: '#f3f4f6', color: '#4b5563', fontWeight: 500 }}>
                {s.icon} {s.short}
              </span>
            ))}
          </div>
        )}

        {/* Scope snapshot */}
        {scope && scope.metrics.length > 0 && (
          <div style={{ background: '#eff6ff', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {scope.metrics.slice(0, 2).map((m, i) => (
                <span key={i} style={{ fontSize: 12, color: '#1e40af', fontWeight: 600 }}>📊 {m}</span>
              ))}
            </div>
          </div>
        )}

        {/* ─── What's Pending (separate section) ─── */}
        {pending.length > 0 && (
          <div style={{ background: '#eff6ff', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1e40af', letterSpacing: 0.5, marginBottom: 4 }}>📋 WHAT'S PENDING</div>
            {pending.map((p, i) => (
              <div key={i} style={{ fontSize: 12, color: '#1e3a5f', lineHeight: 1.4, marginBottom: i < pending.length - 1 ? 4 : 0 }}>
                {p.icon} <strong>{p.service}:</strong> {p.text.length > 90 ? p.text.slice(0, 90) + '…' : p.text}
              </div>
            ))}
          </div>
        )}

        {/* ─── What's Blocked (separate section) ─── */}
        {blocked.length > 0 && (
          <div style={{ background: '#fef2f2', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', letterSpacing: 0.5, marginBottom: 4 }}>🚫 WHAT'S BLOCKED</div>
            {blocked.map((b, i) => (
              <div key={i} style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.4, marginBottom: i < blocked.length - 1 ? 4 : 0 }}>
                {b.icon} <strong>{b.service}:</strong> {b.text.length > 90 ? b.text.slice(0, 90) + '…' : b.text}
              </div>
            ))}
          </div>
        )}

        {/* ─── Why It's Delayed (separate section) ─── */}
        {reasons.length > 0 && (
          <div style={{ background: '#fff7ed', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#c2410c', letterSpacing: 0.5, marginBottom: 4 }}>💡 WHY IT'S DELAYED</div>
            {reasons.map((r, i) => (
              <div key={i} style={{ fontSize: 12, color: '#9a3412', lineHeight: 1.4, marginBottom: i < reasons.length - 1 ? 4 : 0 }}>
                {r.icon} <strong>{r.service}:</strong> {r.flag}
                {r.explanation && <span style={{ color: '#78350f' }}> — {r.explanation.length > 80 ? r.explanation.slice(0, 80) + '…' : r.explanation}</span>}
              </div>
            ))}
          </div>
        )}

        {/* ─── What's Working Well (separate section) ─── */}
        {wins.length > 0 && (
          <div style={{ background: '#f0fdf4', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#166534', letterSpacing: 0.5, marginBottom: 4 }}>✅ WHAT'S WORKING WELL</div>
            {wins.slice(0, 3).map((w, i) => (
              <div key={i} style={{ fontSize: 12, color: '#166534', lineHeight: 1.4, marginBottom: i < Math.min(wins.length, 3) - 1 ? 4 : 0 }}>
                {w.icon} <strong>{w.service}:</strong> {w.text.length > 80 ? w.text.slice(0, 80) + '…' : w.text}
              </div>
            ))}
          </div>
        )}

        {/* ─── Needs Follow-Up (separate section) ─── */}
        {followups.length > 0 && (
          <div style={{ background: '#faf5ff', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#7e22ce', letterSpacing: 0.5, marginBottom: 4 }}>🔔 NEEDS FOLLOW-UP</div>
            {followups.map((f, i) => (
              <div key={i} style={{ fontSize: 12, color: '#581c87', lineHeight: 1.4, marginBottom: i < followups.length - 1 ? 4 : 0 }}>
                {f.icon} <strong>{f.service}:</strong> {f.text.length > 80 ? f.text.slice(0, 80) + '…' : f.text}
              </div>
            ))}
          </div>
        )}

        {/* Bottom */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>
            {project.account_manager}{lastDate ? ` · ${lastDate}` : ''}
          </span>
          <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>View details →</span>
        </div>
      </div>
    </Link>
  );
}

function Loading() {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', color: '#9ca3af' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#111', marginBottom: 8 }}>IncX<span style={{ color: '#6366f1' }}>Ops</span></div>
      <div>Loading projects...</div>
    </div>
  </div>;
}
function ErrorState({ message }) {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', color: '#6b7280' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 4 }}>Something went wrong</div>
      <div style={{ fontSize: 14 }}>{message}</div>
    </div>
  </div>;
}
function EmptyState() {
  return <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', color: '#6b7280' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 4 }}>No projects yet</div>
      <div style={{ fontSize: 14 }}>Data will appear here once forms are submitted</div>
    </div>
  </div>;
}

export default Dashboard;